import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Form } from 'opencare-crown';

import sanitizeString from 'utils/sanitizeString';
import scrollToRef from 'utils/scrollToRef';

import FloatingInput from '../../../common/components/FloatingInput/FloatingInput';
import MultiSelect from '../../../common/components/MultiSelect/MultiSelect';
import {
  AdditionalBlock,
  OptionBlock,
  OptionTitle,
  PrimaryButton,
} from '../../../common/styled-components';
import { ButtonBlock } from '../../styled-components';

import {
  specificConcernsTreatments,
  treatmentCheckupCleaning,
} from './constants';

export const TreatmentSelector = ({ savedTreatments, isOngoingSelected, onContinue }) => {
  const refToAdditionalDetails = useRef();

  const [
    otherTreatmentDescription,
    setOtherTreatment,
  ] = useState(savedTreatments?.find((treatment) => treatment.id === 'other')?.note || '');

  useEffect(() => {
    if (isOtherSelected) {
      scrollToRef(refToAdditionalDetails);
    }
  }, [isOtherSelected]);

  const [treatmentForm] = Form.useForm();

  // Select checkup and cleaning by default if ongoing care was selected
  const [selectedTreatments, setSelectedTreatments] = useState(
    savedTreatments || [],
  );

  // Select checkup and cleaning by default if ongoing care was selected
  useEffect(() => {
    if (isOngoingSelected) {
      selectTreatment(treatmentCheckupCleaning);
    }
  }, [isOngoingSelected, selectTreatment]);

  const selectTreatment = useCallback((choice) => {
    const previouslySelected = selectedTreatments.find(
      (treatment) => treatment.id === choice.id,
    );

    if (previouslySelected) {
      const newTreatments = selectedTreatments.filter(
        (treatment) => treatment.id !== choice.id,
      );

      setSelectedTreatments(newTreatments);
    } else {
      setSelectedTreatments([...selectedTreatments, choice]);
    }
  }, [selectedTreatments]);

  const isOtherSelected = selectedTreatments.find(
    (treatment) => treatment.title === 'Other',
  );

  const onSubmit = () => {
    const note = isOtherSelected ? sanitizeString(otherTreatmentDescription) : null;
    const otherTreatment = selectedTreatments.find((treatment) => treatment.id === 'other');
    if (otherTreatment) {
      otherTreatment.note = note;
    }

    onContinue({
      selectedTreatments,
      otherTreatmentDescription,
    });
  };

  const isSubmitDisabled = () =>
    !selectedTreatments.length || (isOtherSelected && !otherTreatmentDescription.length);

  return (
    <Form onFinish={onSubmit} className="sensitive-data">
      {/* New Patient Exam + Cleaning Option */}
      <OptionBlock className="treatment-selector-option-block treatment-selector-option-block-ongoing-care">
        <MultiSelect
          key={treatmentCheckupCleaning.id}
          name="treatments"
          id={treatmentCheckupCleaning.id}
          title={treatmentCheckupCleaning.title}
          className="treatment-selector-option treatment-checkup-cleaning"
          description={isOngoingSelected
            ? 'All new patients joining a practice require an exam (check up) and cleaning'
            : ''}
          onChange={() => selectTreatment(treatmentCheckupCleaning)}
          columns={1}
          alwaysSelected={isOngoingSelected}
          initialSelected={selectedTreatments.findIndex(
            (saved) => saved.id === treatmentCheckupCleaning.id,
          ) > -1 || isOngoingSelected}
        />
      </OptionBlock>
      {/* Other Options */}
      {specificConcernsTreatments.map((treatmentCategory) => (
        <div key={treatmentCategory.header}>
          <OptionTitle muted margin="25px 0 10px 10px">
            {treatmentCategory.header}
          </OptionTitle>
          <OptionBlock className="treatment-selector-option-block">
            {treatmentCategory.values.map((treatment) => (
              <MultiSelect
                key={treatment.id}
                name="treatments"
                id={treatment.id}
                title={`${treatment.title}${
                  treatment.title === 'Other' ? '...' : ''
                }`}
                className="treatment-selector-option"
                tooltip={treatment.description}
                onChange={() => selectTreatment(treatment)}
                columns={2}
                initialSelected={selectedTreatments.findIndex(
                  (saved) => saved.id === treatment.id,
                ) > -1}
              />
            ))}
            {/* This is a bit of a hacky way to left-align the overflowed option */}
            {treatmentCategory.values.length % 2 !== 0
              && (
                <MultiSelect
                  name="left-align-helper"
                  title="left-align-helper"
                  className="treatment-selector-option"
                  onChange={() => null}
                  columns={2}
                  invisible
                />
              )}
          </OptionBlock>
        </div>
      ))}
      {isOtherSelected
        && (
          <AdditionalBlock
            width={`${720 + 10}px`} // Makes sure it aligns with the other options
            ref={refToAdditionalDetails}
            className="treatment-selector-other-treatment-note"
            tabIndex="-1"
          >
            <FloatingInput
              field="otherTreatment"
              form={treatmentForm}
              label="Describe other treatments"
              initialValue={otherTreatmentDescription}
              testId="other-additional"
              capitalize={false}
              onChange={(e) => setOtherTreatment(e.target.value)}
            />
          </AdditionalBlock>
        )}
      <ButtonBlock className="treatment-selector-continue-button">
        <PrimaryButton
          type="submit"
          disabled={isSubmitDisabled()}
          data-testid="continue"
        >
          {/* If only NPE+C was selected and is selected by default */}
          {selectedTreatments.length === 1 && isOngoingSelected
            ? 'No specific concerns or treatments needed'
            : 'Continue'}
        </PrimaryButton>
      </ButtonBlock>
    </Form>
  );
};

TreatmentSelector.propTypes = {
  savedTreatments: PropTypes.array,
  isOngoingSelected: PropTypes.bool,
  onContinue: PropTypes.func.isRequired,
};

